import { useCallback, useEffect } from 'react';

import { isError } from '@game/utils';
import { socketManager } from '@lib/socketManager';
import type { JSX, ReactNode } from 'react';

import { shouldReloadOnAuthError } from '@util/authErrorReload';
import { useAuthContext } from '@hooks/useAuthContext';
import { AuthStatus } from '@store/authState';
import { authStore } from '@store/authStore';
import { store } from '@store/store';

import { SocketContext } from './socketContext';

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider = ({ children }: SocketProviderProps): JSX.Element => {
  const status = authStore((s) => s.status);
  const { auth } = useAuthContext();

  const { handleGameServerConnected, handleGameServerDisconnected, handleError } = authStore.getState();

  // Cleanup on unmount
  useEffect(() => {
    const handler = (): void => {
      socketManager.disconnect();
    };
    window.addEventListener('beforeunload', handler);
    return (): void => {
      window.removeEventListener('beforeunload', handler);
    };
  }, []);

  // Setup event listeners
  const handleReconnectAttempt = useCallback(
    (attemptNumber: number): void => {
      auth.refreshIdToken().catch((error: unknown) => {
        if (isError(error)) {
          handleError(error);
        }
      });
      store.setState({ reconnectAttempt: attemptNumber });
    },
    [auth, handleError],
  );

  // Setup socket connection and event handlers
  useEffect(() => {
    const validStatus = status === AuthStatus.LOGGED_IN_CONNECTING_TO_GAME_SERVER || status === AuthStatus.LOGGED_IN_CONNECTED_TO_GAME_SERVER;
    if (validStatus) {
      const handleReconnect = (): void => {
        handleGameServerConnected();
        store.setState({ reconnectAttempt: 0 });
      };

      const handleConnectError = (error: Error): void => {
        if (!error.message) {
          return;
        }

        if (error.message.includes('Error verifying token with firebase') || error.message.includes('Error verifying authentication token')) {
          if (shouldReloadOnAuthError()) {
            globalThis.location.reload();
          } else {
            handleError({
              name: 'AuthenticationError',
              message: 'Unable to authenticate. The authentication service may be experiencing issues. Please try again later.',
            });
            handleGameServerDisconnected();
          }
        } else if (error.message.includes('xhr poll error')) {
          handleError({ name: 'ConnectionError', message: 'Unable to connect to the game server.' });
          handleGameServerDisconnected();
        } else {
          handleError(error);
          handleGameServerDisconnected();
        }
      };

      const handleDisconnect = (): void => {
        handleError({ name: 'ConnectionError', message: 'Disconnected from the game server.' });
        handleGameServerDisconnected();
      };

      socketManager.onReconnectAttempt(handleReconnectAttempt);
      socketManager.onReconnect(handleReconnect);
      socketManager.onConnectError(handleConnectError);
      socketManager.onDisconnect(handleDisconnect);

      return (): void => {
        socketManager.offReconnectAttempt(handleReconnectAttempt);
        socketManager.offReconnect(handleReconnect);
        socketManager.offConnectError(handleConnectError);
        socketManager.offDisconnect(handleDisconnect);
      };
    }
  }, [auth, handleGameServerDisconnected, handleError, status, handleReconnectAttempt, handleGameServerConnected]);

  return (
    <SocketContext
      value={{
        sendToServer: socketManager.sendToServer,
        sendToServerPromise: socketManager.sendToServerPromise,
      }}
    >
      {children}
    </SocketContext>
  );
};
